import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../../components/v2/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import { exportRoundsWindowsCsv } from '../../../../services/csvExport.js';
import { convertSecto12Hour, luxonParseSQLDate, luxonCompareDateTimes } from '../../../../libs.jsx';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';
import Pagination from '../../../../components/v2/Pagination.jsx';
import { anchoredPopoverStyle } from '../../../../utils/popoverPosition.js';
import RowActionsMenu from '../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const ROUNDS_COLUMN_DEFS = [
    { key: 'description', label: 'Description', sortable: true, filterable: false, required: true },
    { key: 'window_start_display', label: 'Start Time', sortable: true, filterable: false, required: true },
    { key: 'window_end_display', label: 'End Time', sortable: true, filterable: false, required: true },
    { key: 'interval', label: '# Checks', sortable: true, filterable: false, required: true },
    { key: 'generate_alarm_display', label: 'Alarm On', sortable: true, filterable: true, required: false },
    { key: 'alarm_at_display', label: 'Alarm Time', sortable: true, filterable: false, required: false },
    { key: 'enabled_display', label: 'Enabled', sortable: true, filterable: true, required: false },
    { key: 'area_group_count', label: 'Area Groups', sortable: true, filterable: false, required: false },
];

const DEFAULT_VISIBLE = ROUNDS_COLUMN_DEFS.map((c) => c.key);
const ALL_COLUMN_KEYS = ROUNDS_COLUMN_DEFS.map((c) => c.key);

const COLUMN_LABELS = {};
ROUNDS_COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

// ---------------------------------------------------------------------------
// Group count popover (follows AreasPage ResidentsPopoverCell pattern)
// ---------------------------------------------------------------------------
const GroupCountPopover = ({ groupNames, icon, row, onAssign }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);
    // When onAssign is provided (matches the kebab's "Assign Area Groups"
    // item, which is itself unconditionally available), clicking the badge
    // opens the assign drawer. Hover preview keeps working either way.
    const isClickToAssign = !!onAssign;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const computePosition = () => {
        const style = anchoredPopoverStyle(ref.current, { width: 256, flipBelow: 200, gap: 4 });
        if (style) setPopoverStyle(style);
    };

    const handleOpen = (nextOpen) => {
        if (nextOpen) computePosition();
        setOpen(nextOpen);
    };

    const handleBadgeClick = () => {
        if (isClickToAssign) {
            setOpen(false);
            onAssign(row);
        } else {
            handleOpen(!open);
        }
    };

    return (
        <div className="ws-count-cell" ref={ref}>
            <button
                type="button"
                className="ws-count-badge"
                onClick={handleBadgeClick}
                onMouseEnter={() => handleOpen(true)}
                onMouseLeave={() => handleOpen(false)}
                aria-expanded={isClickToAssign ? undefined : open}
                aria-label={isClickToAssign
                    ? `Manage ${groupNames.length} area group${groupNames.length !== 1 ? 's' : ''}`
                    : `${groupNames.length} group${groupNames.length !== 1 ? 's' : ''} assigned`}
                title={isClickToAssign ? 'Click to manage area groups' : undefined}
            >
                <i className={icon} style={{ fontSize: '12px' }} aria-hidden="true" />
                {groupNames.length}
            </button>
            {open ? (
                <div className="ws-count-popover" style={popoverStyle}>
                    <div className="ws-count-popover__title">Groups ({groupNames.length})</div>
                    <ul className="ws-count-popover__list">
                        {groupNames.map((name, i) => (
                            <li key={i} className="ws-count-popover__item">{name}</li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const RoundsRowActions = ({ window: win, onEdit, onAssignGroups, onDelete }) => {
    return (
        <RowActionsMenu label={`Actions for ${win.description}`} classPrefix="ap">
            {(close) => (
                <>
                    <button type="button" onClick={() => { onEdit(win); close(); }} className="ap-row-actions__item">
                        <i className="fe fe-edit ap-row-actions__item-icon" aria-hidden="true" />
                        Edit Window Settings
                    </button>
                    <button type="button" onClick={() => { onAssignGroups(win); close(); }} className="ap-row-actions__item">
                        <i className="fe fe-map ap-row-actions__item-icon" aria-hidden="true" />
                        Assign Area Groups
                    </button>
                    <button type="button" onClick={() => { onDelete([win.window_id]); close(); }} className="ap-row-actions__item ap-row-actions__item--danger">
                        <i className="fe fe-trash-2 ap-row-actions__item-icon" aria-hidden="true" />
                        Delete
                    </button>
                </>
            )}
        </RowActionsMenu>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const RoundsTab = ({ data, areaGroups, onAdd, onEdit, onAssignGroups, onDelete, saving, myRoleWeight = 10 }) => {
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortColumn, setSortColumn] = useState('description');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('settings-rounds', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    useEffect(() => { setSelectedIds([]); }, [data]);

    // Build group name lookup map from areaGroups prop
    const groupLookup = useMemo(() => {
        const map = {};
        (areaGroups || []).forEach((g) => {
            map[String(g.area_group_id)] = g.area_group_name || `Group ${g.area_group_id}`;
        });
        return map;
    }, [areaGroups]);

    // Enrich data
    const enrichedData = useMemo(() => {
        return (data || []).map((item) => {
            const isEnabled = luxonCompareDateTimes(luxonParseSQLDate(item.ignore_until), 'lt');
            const groupIds = item.area_ids && item.area_ids !== '' ? item.area_ids.split(',') : [];
            const groupNames = groupIds.map((id) => groupLookup[id.trim()] || `Group ${id}`);
            return {
                ...item,
                window_start_display: convertSecto12Hour(item.window_start),
                window_end_display: convertSecto12Hour(item.window_end),
                alarm_at_display: item.generate_alarm === 1 ? convertSecto12Hour(item.alarm_at) : '\u2014',
                generate_alarm_display: item.generate_alarm === 1 ? 'Yes' : 'No',
                enabled_display: isEnabled ? 'Active' : 'Inactive',
                area_group_count: groupIds.length,
                area_group_names: groupNames,
            };
        });
    }, [data, groupLookup]);

    const activeColumns = useMemo(
        () => ROUNDS_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns]
    );

    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        enrichedData.forEach((row) => {
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [enrichedData]);

    // Data pipeline
    const filteredData = useMemo(() => {
        let d = [...enrichedData];
        if (search.trim()) {
            const term = search.toLowerCase();
            d = d.filter((r) =>
                (r.description || '').toLowerCase().includes(term) ||
                (r.window_start_display || '').includes(term) ||
                (r.window_end_display || '').includes(term) ||
                (r.alarm_at_display || '').includes(term)
            );
        }
        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            d = d.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });
        // Sort — use raw seconds for time columns so 12-hour display sorts correctly
        d.sort((a, b) => {
            let aVal, bVal;
            if (sortColumn === 'window_start_display') { aVal = a.window_start ?? 0; bVal = b.window_start ?? 0; }
            else if (sortColumn === 'window_end_display') { aVal = a.window_end ?? 0; bVal = b.window_end ?? 0; }
            else if (sortColumn === 'alarm_at_display') { aVal = a.alarm_at ?? 0; bVal = b.alarm_at ?? 0; }
            else { aVal = a[sortColumn] ?? ''; bVal = b[sortColumn] ?? ''; }
            const result = typeof aVal === 'number' && typeof bVal === 'number'
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
            return sortOrder === 'asc' ? result : -result;
        });
        return d;
    }, [enrichedData, search, filters, sortColumn, sortOrder]);

    useEffect(() => { setPage(1); }, [search, filters, sortColumn, sortOrder]);

    // Pagination
    const totalFiltered = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const paginatedData = filteredData.slice(startIdx, endIdx);

    // Selection
    const allSelected = paginatedData.length > 0 && paginatedData.every((r) => selectedIds.includes(r.window_id));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);

    const handleSelectAll = useCallback(() => {
        const pageIds = paginatedData.map((r) => r.window_id);
        const currentAllSelected = pageIds.every((id) => selectedIds.includes(id));
        if (currentAllSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
        }
    }, [paginatedData, selectedIds]);

    // Handlers
    const handleSort = useCallback((column) => {
        if (sortColumn === column) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        else { setSortColumn(column); setSortOrder('asc'); }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
    }, []);

    const handleClearAllFilters = useCallback(() => { setFilters({}); setSearch(''); }, []);

    const handleExportAll = useCallback(() => {
        if (filteredData.length) exportRoundsWindowsCsv(filteredData, 'rounds-windows.csv', myRoleWeight);
    }, [filteredData, myRoleWeight]);

    const handleBulkExport = useCallback(() => {
        const selected = enrichedData.filter((r) => selectedIds.includes(r.window_id));
        if (selected.length) exportRoundsWindowsCsv(selected, 'rounds-windows-selected.csv', myRoleWeight);
    }, [selectedIds, enrichedData, myRoleWeight]);

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        onDelete(selectedIds);
    };

    // Sort icons
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    // ======================================================================
    // Custom cell rendering
    // ======================================================================
    const renderCell = (row, columnKey) => {
        switch (columnKey) {
            case 'enabled_display': {
                const isActive = row.enabled_display === 'Active';
                return (
                    <span className={`ws-status ${isActive ? 'ws-status--active' : 'ws-status--inactive'}`}>
                        <span className="ws-status__dot" aria-hidden="true" />
                        {row.enabled_display}
                    </span>
                );
            }
            case 'area_group_count': {
                const names = row.area_group_names || [];
                // "None" carries the same action as the badge so the first
                // group can be assigned from here.
                if (names.length === 0) {
                    return (
                        <NoneActionCell
                            className="ap-cell-text--muted"
                            onClick={onAssignGroups ? () => onAssignGroups(row) : undefined}
                            title="Click to assign area groups"
                        />
                    );
                }
                return <GroupCountPopover groupNames={names} icon="fe fe-map" row={row} onAssign={onAssignGroups} />;
            }
            default:
                return <span className="ap-cell-text">{row[columnKey] != null ? row[columnKey] : '\u2014'}</span>;
        }
    };

    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    return (
        <>
            {/* Controls */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input type="search" placeholder="Search rounds windows..." value={search} onChange={(e) => setSearch(e.target.value)} className="ap-search__input" aria-label="Search rounds windows" />
                    </div>
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle columns={ROUNDS_COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export rounds windows">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                    <button type="button" onClick={onAdd} className="ap-btn ap-btn--primary" aria-label="Add rounds window">
                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Add Window
                    </button>
                </div>
            </div>

            <ActiveFilterBar filters={filters} columnLabels={COLUMN_LABELS} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />

            {/* Bulk bar */}
            {selectedIds.length > 0 ? (
                <div className="ap-bulk-bar">
                    <div className="ap-bulk-bar__left">
                        <span className="ap-bulk-bar__count">{selectedIds.length} window{selectedIds.length !== 1 ? 's' : ''} selected</span>
                        <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">Deselect All</button>
                    </div>
                    <div className="ap-bulk-bar__right">
                        <button type="button" onClick={handleBulkExport} className="ap-bulk-btn" aria-label="Export selected">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Export
                        </button>
                        <button type="button" onClick={handleBulkDelete} disabled={saving} className="ap-bulk-btn ap-bulk-btn--danger" aria-label="Delete selected">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Table */}
            {filteredData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters ? 'No rounds windows match your filters.' : 'No rounds windows configured.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="ap-table-wrap">
                        <table className="ap-table">
                            <thead className="ap-thead">
                                <tr>
                                    <th scope="col" className="ap-th ap-th--checkbox">
                                        <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected; }} onChange={handleSelectAll} className="ap-checkbox" aria-label="Select all" />
                                    </th>
                                    <th scope="col" className="ap-th ap-th--actions">Edit</th>
                                    {activeColumns.map((col) => (
                                        <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                            <div className="ap-th__content">
                                                {col.sortable ? (
                                                    <button type="button" onClick={() => handleSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                        {col.label}
                                                        {renderSortIcon(col.key)}
                                                    </button>
                                                ) : <span>{col.label}</span>}
                                                {col.filterable ? (
                                                    <FilterPopover columnKey={col.key} label={col.label} values={getFilterValues(col.key)} selectedValues={filters[col.key] || []} onChange={handleFilterChange} />
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="ap-tbody">
                                {paginatedData.map((row) => {
                                    const isSelected = selectedIds.includes(row.window_id);
                                    return (
                                        <tr key={row.window_id} className={`ap-tr${isSelected ? ' is-selected' : ''}`}>
                                            <td className="ap-td ap-td--checkbox">
                                                <input type="checkbox" checked={isSelected} onChange={() => handleSelectRow(row.window_id)} className="ap-checkbox" aria-label={`Select ${row.description}`} />
                                            </td>
                                            <td className="ap-td ap-td--actions">
                                                <RoundsRowActions window={row} onEdit={onEdit} onAssignGroups={onAssignGroups} onDelete={onDelete} />
                                            </td>
                                            {activeColumns.map((col) => (
                                                <td key={col.key} className="ap-td">
                                                    {renderCell(row, col.key)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <Pagination
                        page={safePage}
                        totalPages={totalPages}
                        total={totalFiltered}
                        perPage={perPage}
                        onPageChange={setPage}
                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                        entityLabel={totalFiltered === 1 ? 'window' : 'windows'}
                        selectId="rounds-per-page"
                    />
                </>
            )}
        </>
    );
};

export default RoundsTab;
